import type { ReactNode } from "react";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { ErrorIconV2 } from "../../Icons";
import { ResponsiveSpinner } from "../../ResponsiveSpinner";
import { Typo } from "../../Typo";
import { withSupportLink } from "./supportLink";
import { useStepUpFlowTranslations } from "./useStepUpFlowTranslations";

export type StepUpPhase = "redirect" | "verifying" | "error";

export const REDIRECT_DELAY_MS = 5000;
export const VERIFYING_DELAY_MS = 800;

type UseStepUpChallengeArgs = {
  onVerified: () => void;
  simulateFailure?: boolean;
  paused?: boolean;
};

export function useStepUpChallenge({
  onVerified,
  simulateFailure = false,
  paused = false,
}: UseStepUpChallengeArgs): { phase: StepUpPhase } {
  const [phase, setPhase] = useState<StepUpPhase>("redirect");
  const onVerifiedRef = useRef(onVerified);
  useEffect(() => {
    onVerifiedRef.current = onVerified;
  });

  useEffect(() => {
    if (phase !== "redirect" || paused) return;
    const id = setTimeout(() => setPhase("verifying"), REDIRECT_DELAY_MS);
    return () => clearTimeout(id);
  }, [phase, paused]);

  useEffect(() => {
    if (phase !== "verifying" || paused) return;
    const id = setTimeout(() => {
      if (simulateFailure) setPhase("error");
      else onVerifiedRef.current();
    }, VERIFYING_DELAY_MS);
    return () => clearTimeout(id);
  }, [phase, simulateFailure, paused]);

  return { phase };
}

type StepUpStateProps = {
  isMobile?: boolean;
};

type StepUpLayoutProps = StepUpStateProps & {
  icon: ReactNode;
  title: string;
  body: ReactNode;
  compact?: boolean;
};

const StepUpLayout = ({
  icon,
  title,
  body,
  isMobile,
  compact,
}: StepUpLayoutProps) => (
  <div
    className={clsx(
      "flex min-h-0 flex-1 flex-col items-start gap-4",
      isMobile && (compact ? "py-3" : "py-10"),
    )}
  >
    {icon}
    <Typo
      element="h2"
      type={isMobile ? "headingLG" : "headingXL"}
      textColor="text-text-primary"
    >
      {title}
    </Typo>
    <Typo element="p" type="bodySM" textColor="text-text-secondary">
      {body}
    </Typo>
  </div>
);

const Spinner = () => (
  <ResponsiveSpinner
    width={48}
    height={48}
    strokeWidth={4}
    aria-hidden="true"
  />
);

export const StepUpRedirect = ({ isMobile }: StepUpStateProps) => {
  const translations = useStepUpFlowTranslations();
  return (
    <StepUpLayout
      isMobile={isMobile}
      icon={<Spinner />}
      title={translations.redirectTitle}
      body={withSupportLink(translations.redirectBody, { tone: "inherit" })}
    />
  );
};

export type StepUpVerifyingAction = "create" | "archive";

export const StepUpVerifying = ({
  isMobile,
  action,
}: StepUpStateProps & { action: StepUpVerifyingAction }) => {
  const translations = useStepUpFlowTranslations();
  return (
    <StepUpLayout
      isMobile={isMobile}
      icon={<Spinner />}
      title={
        action === "create"
          ? translations.verifyingCreateTitle
          : translations.verifyingArchiveTitle
      }
      body={
        isMobile
          ? translations.verifyingBody
          : withSupportLink(translations.verifyingBody, { tone: "inherit" })
      }
    />
  );
};

export const StepUpError = ({ isMobile }: StepUpStateProps) => {
  const translations = useStepUpFlowTranslations();
  return (
    <StepUpLayout
      isMobile={isMobile}
      compact
      icon={
        <ErrorIconV2 className="size-16" aria-hidden="true" focusable="false" />
      }
      title={translations.errorTitle}
      body={withSupportLink(translations.errorBody, { tone: "inherit" })}
    />
  );
};
